import {
  addItemsToSet,
  clearSet,
  removeItemsFromSet,
  toggleItemsInSet,
} from '@hooks/useSet'

/**
 * Creates a set of operations for manipulating a Set within a state object
 *
 * @param key - The key of the state object that contains a Set
 * @returns A function that takes state and setState function and returns an object with set operations
 *
 * @example
 * // Types are inferred automatically
 * const hiddenDaysOps = createSetOperationsFor('hiddenDays')(state, setState)
 */
export function createSetOperationsFor<K extends string>(key: K) {
  return <S extends Record<K, Set<unknown>>>(
    state: S,
    setState: (updater: (prev: S) => S) => void,
  ) => {
    // Infer the element type from the Set
    type E = S[K] extends Set<infer U> ? U : never

    // Get set value with appropriate type
    const getValue = (obj: S): Set<E> => obj[key] as unknown as Set<E>

    return {
      /**
       * The current value of the Set
       */
      value: getValue(state),

      /**
       * Replace the Set with a new Set
       */
      set: (items: Set<E>) => {
        setState(
          (prev) =>
            ({
              ...prev,
              [key]: items,
            } as S),
        )
      },

      /**
       * Add one or more items to the Set
       */
      add: (items: E | E[]) => {
        setState(
          (prev) =>
            ({
              ...prev,
              [key]: addItemsToSet(items)(getValue(prev)),
            } as S),
        )
      },

      /**
       * Remove one or more items from the Set
       */
      remove: (items: E | E[]) => {
        setState(
          (prev) =>
            ({
              ...prev,
              [key]: removeItemsFromSet(items)(getValue(prev)),
            } as S),
        )
      },

      /**
       * Clear all items from the Set
       */
      clear: () => {
        setState(
          (prev) =>
            ({
              ...prev,
              [key]: clearSet(getValue(prev)),
            } as S),
        )
      },

      /**
       * Toggle the presence of one or more items in the Set
       * (add if not present, remove if present)
       */
      toggle: (items: E | E[]) => {
        setState(
          (prev) =>
            ({
              ...prev,
              [key]: toggleItemsInSet(items)(getValue(prev)),
            } as S),
        )
      },
    }
  }
}
